import { AppColors } from "@/styles/colors";
import {
  Combobox,
  Field,
  Portal,
  Spinner,
  Text,
  useFilter,
  useListCollection,
  useMediaQuery,
} from "@chakra-ui/react";
import { Control, FieldValues, useController } from "react-hook-form";
import { LightMode } from "../ui/color-mode";
import { v4 } from "uuid";
import { useEffect } from "react";

interface Props {
  control: Control<FieldValues>;
  name: string;
  onChange?: (e: Combobox.InputValueChangeDetails) => void;
  onSelect?: (value?: string) => void;
  disablePortal?: boolean;
  placeholder?: string;
  items: any[];
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  clearable?: boolean;
  required?: boolean;
  menuMaxHeight?:
    | string
    | { xs?: string; sm?: string; md?: string; lg?: string; xl?: string };
  inputValue?: string;
  clearOutside?: boolean;
  noResultText?: string;
  fullWidth?: boolean;
  minHeight?: string;
}

export const ComboboxComponent = ({
  control,
  name,
  disablePortal,
  placeholder,
  items,
  label,
  onChange,
  onSelect,
  menuMaxHeight,
  loading,
  disabled,
  clearable = true,
  required = false,
  clearOutside = true,
  inputValue,
  noResultText = "No hay resultados",
  fullWidth = false,
  minHeight,
}: Props) => {
  const { contains } = useFilter({ sensitivity: "base" });
  const { collection, filter } = useListCollection<{
    value: string;
    label: string;
  }>({
    initialItems: items,
    filter: contains,
  });

  useEffect(() => {
    collection.setItems(items as { value: string; label: string }[]);
  }, [items, collection]);

  const [isXS, isSM, isMD, isLG, isXL] = useMediaQuery([
    "(max-width: 1350px)",
    "(min-width: 1351px) and (max-width: 1414px)",
    "(min-width: 1415px) and (max-width: 1590px)",
    "(min-width: 1591px) and (max-width: 1696px)",
    "(min-width: 1900px)",
  ]);

  const getSelectMaxH = () => {
    if (typeof menuMaxHeight === "string") return menuMaxHeight;
    if (typeof menuMaxHeight === "object" && isXS) return menuMaxHeight?.xs;
    if (typeof menuMaxHeight === "object" && isSM) return menuMaxHeight?.sm;
    if (typeof menuMaxHeight === "object" && isMD) return menuMaxHeight?.md;
    if (typeof menuMaxHeight === "object" && isLG) return menuMaxHeight?.lg;
    if (typeof menuMaxHeight === "object" && isXL) return menuMaxHeight?.xl;
  };

  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  const handleInputChange = (details: Combobox.InputValueChangeDetails) => {
    filter(details.inputValue);
    onChange?.(details);
  };

  return (
    <Field.Root invalid={!!error} width={fullWidth ? "100%" : "320px"}>
      {
        label && (
          <Field.Label>
            <Text display="flex" gap="4px" fontWeight={600}>
              {label}{" "}
              {required && (
                <Text as="span" fontWeight={600} color={AppColors.REQUIRED}>
                  *
                </Text>
              )}
            </Text>
          </Field.Label>
        )
      }
     

      <Combobox.Root
        collection={collection}
        value={field.value ? [field.value] : []}
        onValueChange={({ value }) => {
          const newVal = value[0] || "";
          field.onChange(newVal);
          onSelect?.(newVal || undefined);
        }}
        onInputValueChange={handleInputChange}
        onInteractOutside={(event) => {
          field.onBlur();
          if (!clearOutside) event.preventDefault();
        }}
        disabled={disabled}
      >
        <Combobox.Control>
          <Combobox.Input
            paddingX="12px"
            // Reservar espacio para los iconos (clear, loading, trigger)
            paddingRight={clearable || loading ? "64px" : "40px"}
            placeholder={placeholder}
            value={inputValue}
            minH={minHeight}
            css={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          />
          <Combobox.IndicatorGroup paddingX="12px">
            {clearable && <Combobox.ClearTrigger />}
            {loading && (
              <Spinner
                data-testid="combobox-loading"
                size="xs"
                borderWidth="1.5px"
                color="fg.muted"
              />
            )}

            <Combobox.Trigger />
          </Combobox.IndicatorGroup>
        </Combobox.Control>

        <Portal disabled={disablePortal}>
          <LightMode>
            <Combobox.Positioner width="100%">
              <Combobox.Content maxHeight={getSelectMaxH()}>
                <Combobox.Empty>
                  <Text
                    paddingX="12px"
                    paddingY="2px"
                    css={{
                      "@media (max-width: 1350px) and (min-width: 1272px)": {
                        fontSize: "10px",
                      },
                      "@media (max-width: 1414px) and (min-width: 1351px)": {
                        fontSize: "12px",
                      },
                      "@media (max-width: 1590px) and (min-width: 1415px)": {
                        fontSize: "16px",
                      },
                      "@media (max-width: 1696px) and (min-width: 1591px)": {
                        fontSize: "16px",
                      },
                      "@media (max-width: 1900px)": {
                        fontSize: "20px",
                      },
                    }}
                  >
                    {noResultText}
                  </Text>
                </Combobox.Empty>
                {collection.items.map((item, index) => (
                  <Combobox.Item
                    paddingX="12px"
                    paddingY="2px"
                    item={item}
                    key={v4()}
                    data-testid={`combobox-content-${index}`}
                  >
                    <Text
                      css={{
                        "@media (max-width: 1350px) and (min-width: 1272px)": {
                          fontSize: "10px",
                        },
                        "@media (max-width: 1414px) and (min-width: 1351px)": {
                          fontSize: "12px",
                        },
                        "@media (max-width: 1590px) and (min-width: 1415px)": {
                          fontSize: "16px",
                        },
                        "@media (max-width: 1696px) and (min-width: 1591px)": {
                          fontSize: "16px",
                        },
                        "@media (max-width: 1900px)": {
                          fontSize: "20px",
                        },
                      }}
                    >
                      {item.label}
                    </Text>
                    <Combobox.ItemIndicator />
                  </Combobox.Item>
                ))}
              </Combobox.Content>
            </Combobox.Positioner>
          </LightMode>
        </Portal>
      </Combobox.Root>

      {!!error && (
        <Field.ErrorText>{String(error?.message)}</Field.ErrorText>
      )}
    </Field.Root>
  );
};
